<template>
  <div v-if="tripStore.isLoading">
    <span class="loading loading-spinner loading-lg text-base-content/60"></span>
  </div>
  <div v-else class="relative">
    <!-- Sticky Steps -->
    <div class="sticky top-0 z-10 bg-base-200 text-center pb-6">
      <ul class="steps steps-vertical lg:steps-horizontal">
        <li :class="['step', validateStep(1) ? 'step-primary' : '']">
          Bendroji informacija
        </li>
        <li :class="['step', validateStep(2) ? 'step-primary' : '']">
          Įtraukti keleivius
        </li>
        <li :class="['step', validateStep(3) ? 'step-primary' : '']">
          Maršruto vietos
        </li>
        <li
          :class="[
            'step',
            validateStep(4) || validateStep(5) ? 'step-primary' : '',
          ]"
          class="step"
        >
          Planuoti
        </li>
        <li class="step">Patvirtinti kelionę</li>
      </ul>
    </div>

    <!-- General Info Sections -->
    <div class="collapse collapse-arrow bg-base-100 rounded-lg mb-6">
      <input type="checkbox" checked />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Bendroji informacija
      </div>
      <div class="collapse-content">
        <div class="px-2 mt-3">
          <div class="grid grid-cols-1 gap-4 mb-6">
            <div>
              <template v-if="availableTravelsByDate">
                <Alert
                  v-if="availableTravelsByDate.message"
                  :key="tripInfo.tripDate"
                  :text="availableTravelsByDate.message"
                  :alert-class="
                    availableTravelsByDate.hasConflict ? 'alert-warning' : ''
                  "
                  :icon="
                    availableTravelsByDate.hasConflict ? 'warning' : 'info'
                  "
                  :dismissible="false"
                />
                <div v-if="availableTravelsByDate.travels" class="mb-6">
                  <TravelItems
                    v-for="travel in availableTravelsByDate.travels"
                    :key="travel.id"
                    :travel="travel"
                  />
                </div>
              </template>
              <span class="flex items-center mb-2">Pavėžėjimo data*</span>
              <input
                v-model="tripInfo.tripDate"
                type="date"
                placeholder="Vardenis"
                class="input w-full md:w-56"
                required
              />
            </div>
            <div>
              <span class="flex items-center mb-2">Pavėžėjimo pagrindas*</span>
              <select
                v-model="tripInfo.basisType"
                class="select w-full"
                required
              >
                <option
                  v-for="[key, value] in Object.entries(basisTypeConfig)"
                  :key="key"
                  :value="key"
                >
                  {{ value }}
                </option>
              </select>
            </div>
            <div>
              <span class="flex items-center mb-2">Pavėžėjimo pastaba</span>
              <textarea
                v-model="tripInfo.notice"
                placeholder="Pastabos, papildoma informacija, komentarai"
                class="textarea w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Passengers Sections -->
    <div
      v-show="validateStep(1)"
      class="collapse collapse-arrow bg-base-100 rounded-lg mb-6"
    >
      <input type="checkbox" checked />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Įtraukti keleivius
      </div>
      <div class="collapse-content">
        <div class="px-2 mt-3">
          <PassengerSearchRegister @updatePassengers="handleUpdatePassengers" />
        </div>

        <!-- List of Passengers -->
        <div v-if="tripInfo.passengers.length > 0" class="mt-6">
          <hr class="my-6" />
          <div class="overflow-x-auto">
            <table class="table">
              <!-- Table Headings -->
              <thead>
                <tr>
                  <th>Vardas Pavardė</th>
                  <th>Kontaktai</th>
                  <th>Papildoma informacija</th>
                  <th>Veiksmai</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(passenger, index) in tripInfo.passengers"
                  :key="index"
                >
                  <td>
                    {{ passenger.firstName }}
                    {{ passenger.lastName }}
                  </td>
                  <td>
                    <div class="flex flex-col gap-1">
                      <div>
                        {{ passenger.phone }}
                        <span v-if="passenger.email">
                          , {{ passenger.email }}
                        </span>
                      </div>
                      <div v-if="passenger.address">
                        {{ passenger.address }}
                      </div>
                    </div>
                  </td>
                  <td class="text-sm">
                    <div v-if="passenger.assistantNeeded" class="mb-1">
                      Reikalingas palydėjimas
                    </div>
                    <div v-if="passenger.valid_until" class="mb-1">
                      Paslauga galioja iki: {{ passenger.valid_until }}
                    </div>
                    <span
                      v-if="passenger.service_restricted"
                      class="text-error mb-1"
                    >
                      Apribotas paslaugų teikimas iki:
                      {{ passenger.service_restriction_until }}.
                    </span>
                    <div v-if="passenger.service_type" class="mb-1">
                      Paslaugų teikimo pagrindas:
                      {{
                        referenceDataStore.clientServiceTypeConfig[
                          passenger.service_type
                        ]?.text
                      }}
                    </div>
                    <div v-if="passenger.disabled" class="mb-1">
                      Neįgaliojo vežimėlyje
                    </div>
                    <div v-if="passenger.assistantPersons > 0" class="mb-1">
                      Lydintis asmuo
                    </div>
                    <div v-if="passenger.child_seat_needed" class="mb-1">
                      Reikalinga vaikiška kėdutė
                    </div>
                    <div v-if="passenger.wheelchair_needed" class="mb-1">
                      Reikalingas neįgaliojo vežimėlis
                    </div>
                    <div v-if="passenger.description">
                      Keleivio pastaba: {{ passenger.description }}
                    </div>
                  </td>

                  <!-- Remove Button -->
                  <td>
                    <DefaultIcon
                      @click="removePassenger(index)"
                      name="xMark"
                      class="btn btn-circle btn-ghost btn-sm text-base-content/70"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Route Sections -->
    <div
      v-show="validateStep(2)"
      class="collapse collapse-arrow bg-base-100 rounded-lg mb-6"
    >
      <input type="checkbox" checked />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Maršruto vietos
      </div>
      <div class="collapse-content">
        <div class="px-2 mt-3">
          <TripWaypointsRegister
            @updateTripWaypoints="handleUpdateTripWaypoints"
          />
        </div>
        <div v-if="tripInfo.tripWaypoints.length > 0" class="px-2 mt-6">
          <div class="overflow-x-auto">
            <table class="table">
              <!-- Table Headings -->
              <thead>
                <tr>
                  <th>Adresas</th>
                  <th>Veiksmai</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(tripWaypoint, index) in tripInfo.tripWaypoints"
                  :key="index"
                >
                  <!-- Address -->
                  <td>
                    {{ tripWaypoint.address }}
                    <div
                      v-if="tripWaypoint.isInRestriction"
                      class="badge badge-warning gap-2 ml-4 p-1"
                    >
                      ! patenka į buferį
                    </div>
                  </td>

                  <!-- Remove Button -->
                  <td>
                    <DefaultIcon
                      @click="removeTripWaypoint(index)"
                      name="xMark"
                      class="btn btn-circle btn-ghost btn-sm text-base-content/70"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-if="tripInfo && tripInfo.tripRoute" class="my-6 px-2">
          <p class="font-medium mb-3">Maršruto informacija</p>
          <ol class="text-sm">
            <li>
              Maršruto ilgis (km):
              {{ tripInfo.tripRoute.length?.toFixed(2) ?? "Nėra duomenų" }}
            </li>
            <li>
              Maršruto trukmė (val):
              {{
                tripInfo.tripRoute.time
                  ? secondsToHours(tripInfo.tripRoute.time)
                  : "Nėra duomenų"
              }}
            </li>
          </ol>
        </div>
        <div class="px-2 mt-3">
          <Map
            :waypoints-geometry="tripInfo.tripWaypoints"
            :route-geometry="routeGeometry"
            :center="[21.39, 55.71]"
            :zoom="10"
            class="rounded-md"
          />
        </div>
      </div>
    </div>

    <!-- Planning Sections -->
    <div
      v-show="validateStep(3)"
      class="collapse collapse-arrow bg-base-100 rounded-lg mb-6"
    >
      <input type="checkbox" checked />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Planuoti kelionę
      </div>
      <div class="collapse-content">
        <div class="px-2">
          <!-- Container for all options -->
          <div class="w-full max-w-4xl">
            <!-- Grid container for responsive layout -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <!-- Option 1: Create New Route -->
              <label
                @click="selectRouteMode('create')"
                class="block cursor-pointer"
              >
                <input
                  type="radio"
                  name="option"
                  class="hidden peer"
                  :checked="routeMode === 'create'"
                />
                <div
                  class="flex items-start gap-4 p-4 h-full rounded-lg border border-base-300 hover:border-base-300 peer-checked:border-primary peer-checked:bg-primary-content transition-all duration-200"
                >
                  <!-- Icon: Plus Circle for New Route -->
                  <div class="shrink-0 mt-1">
                    <DefaultIcon
                      name="plusSquare"
                      size="w-6 h-6"
                      color="#673ab7"
                    />
                  </div>
                  <!-- Content -->
                  <div>
                    <h3 class="font-medium text-base-content">
                      Kurti naują kelionę
                    </h3>
                    <p class="text-sm text-base-content/60">
                      Pasirinkite transporto priemonę, vairuotoją ir sukurkite
                      naują dienos kelionę.
                    </p>
                  </div>
                </div>
              </label>

              <!-- Option 2: Edit Existing Route -->
              <label
                @click="selectRouteMode('edit')"
                class="block cursor-pointer"
                v-if="availableTravelsByDate.hasConflict"
              >
                <input
                  type="radio"
                  name="option"
                  class="hidden peer"
                  :checked="routeMode === 'edit'"
                />
                <div
                  class="flex items-start gap-4 p-4 h-full rounded-lg border border-base-300 hover:border-base-300 peer-checked:border-primary peer-checked:bg-primary-content transition-all duration-200"
                >
                  <!-- Icon: Edit Route -->
                  <div class="shrink-0 mt-1">
                    <DefaultIcon
                      name="editPencil"
                      size="w-6 h-6"
                      color="#673ab7"
                    />
                  </div>
                  <!-- Content -->
                  <div>
                    <h3 class="font-medium text-base-content">
                      Įtraukti į esamą kelionę
                    </h3>
                    <p class="text-sm text-base-content/60">
                      Įtraukite pavėžėjimą į jau suplanuotos kelionės maršrutą
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- 
            
          Mode for creating new Route 
          
        -->

        <div v-if="routeMode === 'create'" class="px-2">
          <hr class="my-6 border-base-300" />

          <div class="mb-6">
            <!-- Loading state -->
            <div v-if="vehicleStore.isLoading" class="text-center py-4">
              Ieškoma transporto priemonių...
            </div>

            <div v-else>
              <VehicleSelector
                :available-vehicles="availableVehicles"
                :selected-vehicle-id="tripInfo.selectedVehicle"
                @select-vehicle="handleVehicleSelection"
              />
              <hr />
            </div>

            <!-- Select driver -->
            <div v-if="selectedVehicle.id">
              <div v-if="driverStore.isLoading" class="text-center py-4">
                Ieškoma laisvų vairuotojų...
              </div>
              <div v-else>
                <DriverSelector
                  :available-drivers="availableDrivers"
                  :selected-driver-id="tripInfo.selectedDriver"
                  @select-driver="handleDriverSelection"
                />
              </div>
            </div>

            <!-- Select time -->
            <div
              v-if="tripInfo.selectedVehicle && tripInfo.selectedDriver"
              class="px-2 mb-6"
            >
              <hr class="my-6 border-base-300" />

              <p class="flex items-center mb-2">Pasirinkite kelionės laikus:</p>
              <div
                v-if="tripInfo.tripWaypoints.length > 0"
                class="overflow-x-auto"
              >
                <table class="table">
                  <!-- Table Headings -->
                  <thead>
                    <tr>
                      <th>Adresas</th>
                      <th>Laikas</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Pradžia: {{ tripInfo.travelAddress }}</td>
                      <td>{{ tripInfo.travelTimeStart }}</td>
                    </tr>
                    <tr
                      v-for="(tripWaypoint, index) in tripInfo.tripWaypoints"
                      :key="index"
                    >
                      <!-- Address -->
                      <td>
                        {{ tripWaypoint.address }}
                      </td>

                      <td>
                        <VueDatePicker
                          v-model="tripWaypoint.time"
                          @update:model-value="calcWaypointTimes(index)"
                          time-picker
                          :teleport="true"
                          model-type="HH:mm"
                          hide-input-icon
                          format="HH:mm"
                          placeholder="--:--"
                          cancelText="atšaukti"
                          selectText="pasirinkti"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Pabaiga: {{ tripInfo.travelAddress }}</td>
                      <td>{{ tripInfo.travelTimeFinish }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <Alert
                v-if="overlapingTravels?.hasConflict"
                :text="overlapingTravels.message"
                alert-class="alert-warning"
                icon="warning"
                :dismissible="false"
                class="mx-2"
              />

              <div
                v-if="validateStep('checkWaypointTime')"
                class="flex items-center px-2 mt-6 gap-4"
              >
                <button class="btn btn-primary" @click="planNewTrip">
                  Planuoti naują kelionę
                </button>
                <div class="flex items-center gap-2">
                  <span class="text-sm text-base-content/70">
                    Į kelionę bus įtrauktas atstumas ir laikas nuo transporto
                    priemonės buvimo vietos iki suplanuotos kelionės pradžios ir
                    pabaigos
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mode for adding to existins Route -->

        <div v-if="routeMode === 'edit'" class="px-2 mt-6">
          <!-- Select time for planning -->
          <div>
            <hr class="my-6 border-base-300" />
            <p class="flex items-center mb-2">Pasirinkite kelionės laikus:</p>
            <div
              v-if="tripInfo.tripWaypoints.length > 0"
              class="overflow-x-auto mb-3"
            >
              <table class="table">
                <!-- Table Headings -->
                <thead>
                  <tr>
                    <th>Adresas</th>
                    <th>Laikas</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(tripWaypoint, index) in tripInfo.tripWaypoints"
                    :key="index"
                  >
                    <!-- Address -->
                    <td>
                      {{ tripWaypoint.address }}
                    </td>

                    <td>
                      <VueDatePicker
                        v-model="tripWaypoint.time"
                        @update:model-value="calcWaypointTimes(index)"
                        time-picker
                        :teleport="true"
                        model-type="HH:mm"
                        hide-input-icon
                        format="HH:mm"
                        placeholder="--:--"
                        cancelText="atšaukti"
                        selectText="pasirinkti"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <button
              v-if="validateStep('checkWaypointTime')"
              class="btn btn-primary"
              @click="checkAvailableTravels"
            >
              Ieškoti tinkamų kelionių
            </button>
          </div>

          <div v-if="availableTravels.travels?.length > 0">
            <hr class="my-6 border-base-300" />
            <div class="my-3 space-y-2">
              <div
                v-for="travel in availableTravels.travels"
                :key="travel.id"
                class="p-2 border rounded-lg"
                :class="[
                  selectedTravel?.id === travel.id
                    ? 'bg-primary-content border-primary'
                    : 'bg-base-100',
                ]"
              >
                <div class="flex items-center">
                  <DefaultIcon
                    @click="selectTravel(travel.id)"
                    name="plus"
                    size="w-6 h-6"
                    class="btn btn-circle btn-ghost text-primary btn-sm mr-3"
                    title="Įtraukti pavėžėjimą į kelionę"
                  />
                  <div>
                    <div>Kelionė #{{ travel.id }} ({{ travel.date }})</div>
                    <div>
                      Transporto priemonė, vairuotojas:
                      {{ travel.vehicle.info }} ({{ travel.driver.info }})
                    </div>
                    <div>Organizacija: {{ travel.organization.info }}</div>
                  </div>
                </div>
              </div>
            </div>

            <Alert
              v-if="overlapingTravels?.hasConflict"
              :text="overlapingTravels.message"
              alert-class="alert-warning"
              icon="warning"
              :dismissible="false"
              class="mb-3"
            />

            <div v-if="travelTimeLineInfo && !overlapingTravels.hasConflict">
              <hr class="my-6 border-base-300" />
              <Timeline :waypoints="travelTimeLineInfo" />
              <div
                v-if="travelUpdateInfo && travelUpdateInfo.summary"
                class="mt-6 w-full md:w-1/2"
              >
                <div class="card bg-base-200 shadow-xs">
                  <div class="card-body">
                    <div class="flex items-center gap-2">
                      <h3 class="card-title text-lg">
                        Planuojamas kelionės laikas ir atstumas
                      </h3>
                    </div>
                    <p>
                      Laikas:
                      {{
                        travelUpdateInfo.combined_waypoints.find(
                          (wp) => wp.type === "start",
                        )?.timePlanning
                      }}
                      -
                      {{
                        travelUpdateInfo.combined_waypoints.find(
                          (wp) => wp.type === "finish",
                        )?.timePlanning
                      }}
                    </p>
                    <p>Atstumas: {{ travelUpdateInfo.summary.length }} km</p>
                    <div
                      v-if="travelUpdateInfo.summary.length > 400"
                      class="badge badge-warning gap-2"
                    >
                      Ilga kelionė
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="travelUpdateInfo && travelUpdateInfo.is_valid"
                class="mb-3 mt-6"
              >
                <div class="mt-6">
                  <label class="label text-base-content">
                    <input
                      type="checkbox"
                      v-model="tripInfo.send_sms_registration"
                      class="checkbox"
                    />
                    Informuoti pavėžėjimo keleivius patvirtinimo SMS žinute
                  </label>
                </div>
                <div class="mt-6">
                  <label class="label text-base-content">
                    <input
                      type="checkbox"
                      v-model="tripInfo.send_sms_reminder"
                      class="checkbox"
                    />
                    Informuoti pavėžėjimo keleivius priminimo žinute
                  </label>
                </div>
                <div class="flex items-center mb-3 mt-6">
                  <button
                    class="btn btn-success"
                    @click="addTripToTravel(false)"
                  >
                    Išsaugoti kelionę
                  </button>
                  <button class="btn ml-3" @click="addTripToTravel(true)">
                    Išsaugoti ir kurti naują
                  </button>
                  <button class="btn ml-3" @click="addTripToTravel(true, true)">
                    Išsaugoti ir kurti atgal
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Review Sections -->
    <div
      v-show="validateStep(4)"
      class="collapse collapse-arrow bg-base-100 rounded-lg mb-6"
    >
      <input type="checkbox" checked />
      <div class="collapse-title text-xl font-semibold text-base-content px-6">
        Patvirtinti kelionę
      </div>
      <div class="collapse-content">
        <div class="px-2 mt-3">
          <TripSummary :tripData="tripInfo" />
          <div class="mt-6">
            <label class="label text-base-content">
              <input
                type="checkbox"
                v-model="tripInfo.send_sms_registration"
                class="checkbox"
              />
              Informuoti pavėžėjimo keleivius SMS žinute
            </label>
          </div>
          <div class="mt-6">
            <label class="label text-base-content">
              <input
                type="checkbox"
                v-model="tripInfo.send_sms_reminder"
                class="checkbox"
              />
              Informuoti pavėžėjimo keleivius priminimo žinute
            </label>
          </div>
          <div class="flex items-center mb-3 mt-6">
            <button class="btn btn-success" @click="saveTrip(false)">
              Išsaugoti kelionę
            </button>
            <button class="btn ml-3" @click="saveTrip(true)">
              Išsaugoti ir kurti naują
            </button>
            <button class="btn ml-3" @click="saveTrip(true, true)">
              Išsaugoti ir kurti atgal
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Toast ref="toast" />
</template>

<script setup>
import { ref, watch, onMounted, defineProps } from "vue";
import { useRouter, useRoute } from "vue-router";

import { useVehicleStore } from "@/stores/vehicles";
import { useAuthStore } from "@/stores/auth";
import { useTripStore } from "@/stores/trips";
import { useTravelStore } from "@/stores/travels";
import { useDriverStore } from "@/stores/drivers";
import { useReferenceDataStore } from "@/stores/referenceData";

import { useTimeManagement } from "@/composables/time/useTimeManagement";
import { useRouting } from "@/composables/routing/useRouting";
import { useRouteCalculation } from "@/composables/routing/useRouteCalculation";

import PassengerSearchRegister from "@/components/forms/PassengerSearchRegister.vue";
import TripWaypointsRegister from "@/components/forms/TripWaypointsRegister.vue";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import TripSummary from "@/components/trips/TripSummary.vue";
import Alert from "@/components/ui/alerts/DefaultAlert.vue";
import Toast from "@/components/ui/alerts/ToastComponent.vue";
import Timeline from "@/components/timelines/TimelineComponent.vue";
import Map from "@/components/maps/MapComponent.vue";
import TravelItems from "@/components/travels/TravelItems.vue";
import VehicleSelector from "@/components/vehicles/VehicleSelector.vue";
import DriverSelector from "@/components/drivers/DriverSelector.vue";

import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

const travelStore = useTravelStore();
const tripStore = useTripStore();
const driverStore = useDriverStore();
const referenceDataStore = useReferenceDataStore();

const { calculateRoutingInfo } = useRouting();
const { calculateRoute } = useRouteCalculation();

const toast = ref(null);

const router = useRouter();
const route = useRoute();

/*
  Initialization of form
*/

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: (value) => ["create", "view", "edit"].includes(value),
  },
  tripId: {
    type: [String, Number],
    default: null,
  },
});

/*
  Configurations
*/

const basisTypeConfig = {
  tripBasisType1: "SPIS",
  tripBasisType2: "Hemodializė",
  tripBasisType3: "Virš 2 km. iki stotelės",
  tripBasisType4: "Gulintis",
  tripBasisType5: "Kita",
};

const tripInfo = ref({
  tripDate: null,
  basisType: null,
  notice: "",
  passengers: [],
  tripWaypoints: [],
  tripRoute: [],
  selectedVehicle: null,
  selectedVehicleInfo: null,
  selectedDriver: null,
  selectedDriverInfo: null,
  travelId: null,
  travelLength: null,
  travelAddress: null,
  travelLat: null,
  travelLng: null,
  travelTimeStart: null,
  travelTimeFinish: null,
  travelDurationFromStart: 0,
  travelDurationToFinish: 0,
  send_sms_registration: true,
  send_sms_reminder: true,
});

/*
  Handle emitted passengers
*/

const handleUpdatePassengers = (updatedPassengers) => {
  availableTravels.value = [];
  routeMode.value = null;

  tripInfo.value.passengers = [
    ...tripInfo.value.passengers, // Keep existing passengers
    ...updatedPassengers, // Add new passengers
  ];
};

// Remove passenger by index
const removePassenger = (index) => {
  tripInfo.value.passengers.splice(index, 1);
};

/*
  Handle emitted trip
*/

const routeGeometry = ref(null);

const handleUpdateTripWaypoints = async (updatedTripWaypoints) => {
  tripInfo.value.tripWaypoints = [
    ...tripInfo.value.tripWaypoints,
    ...updatedTripWaypoints,
  ];

  try {
    availableTravels.value = [];
    routeMode.value = null;
    updateTravelRoute();
  } catch (error) {
    console.error("Error updating trip waypoints");
    // Handle error appropriately - maybe emit an event or show a notification
  }
};

// Remove tripWaypoint by index
const removeTripWaypoint = (index) => {
  tripInfo.value.tripWaypoints.splice(index, 1);
};

const travelRouteInfo = ref([]);

const updateTravelRoute = async () => {
  try {
    if (tripInfo.value.tripWaypoints.length > 1) {
      // 2. Construct waypoints
      let waypoints = tripInfo.value.tripWaypoints.map((waypoint) => ({
        lat: parseFloat(waypoint.lat),
        lon: parseFloat(waypoint.lng),
      }));

      // Check if travel coordinates exist
      if (tripInfo.value.travelLat && tripInfo.value.travelLng) {
        const travelPoint = {
          lat: parseFloat(tripInfo.value.travelLat),
          lon: parseFloat(tripInfo.value.travelLng),
        };

        // Add travel point as first and last location
        waypoints = [travelPoint, ...waypoints, travelPoint];
      }

      // 2. Construct paylod
      const routingPayload = {
        locations: waypoints,
        costing: "auto",
        directions_options: {
          units: "kilometers",
        },
      };

      // 3. Calculate the route
      const { trip, geoJsonGeometry } = await calculateRoutingInfo(
        routingPayload,
      );

      // 4. Update the route geometry - the map will react to this change
      routeGeometry.value = geoJsonGeometry;

      // 5. Update trip info with the route summary
      tripInfo.value.tripRoute = trip.summary;
      travelRouteInfo.value = trip;

      calcWaypointTimes();
    } else {
      routeGeometry.value = null;
      tripInfo.value.tripRoute = [];
    }
  } catch (error) {
    console.error("Error updating trip waypoints");
    // Handle error appropriately - maybe emit an event or show a notification
  }
};

/*
  Handle new trip and travel creation
*/

const tripInfoReview = ref(false);
const overlapingTravels = ref([]);

const planNewTrip = async () => {
  try {
    const params = {
      date: tripInfo.value.tripDate,
      driverId: tripInfo.value.selectedDriver,
      vehicleId: tripInfo.value.selectedVehicle,
      startTime: tripInfo.value.travelTimeStart,
      finishTime: tripInfo.value.travelTimeFinish,
    };

    // Check travels overlaping
    overlapingTravels.value = await travelStore.checkTravelsOverlap(params);
  } catch (error) {
    // Handle any potential errors here
    console.error("Error checking available travels");
  }

  if (tripInfo.value.tripDate) {
    tripInfoReview.value = true;
  } else {
    alert("Neužpildyti būtini laukai.");
  }
};

/*
  Time logic
*/

const {
  addMinutesToTime,
  subtractMinutesFromTime,
  // getTodayDate,
  getTimeDifferenceInMinutes,
  secondsToHours,
} = useTimeManagement();

// Calculate waypoint times based on the source waypoint
const calcWaypointTimes = (index) => {
  // Reset available travel because time changes
  availableTravels.value = [];
  overlapingTravels.value = [];
  tripInfoReview.value = false;
  selectedTravel.value = null;

  if (tripInfo.value.tripWaypoints.length < 2) return;

  let travelRouteLegsCount = travelRouteInfo.value?.legs?.length || 0;

  let timeBetween = 0;
  if (travelRouteLegsCount > 1) {
    timeBetween =
      Math.ceil(travelRouteInfo.value.legs[1].summary.time / 60) +
      calculateBufferTime();
  } else {
    timeBetween =
      Math.ceil(tripInfo.value.tripRoute.time / 60) + calculateBufferTime();
  }

  // const timeDifference = getRequiredTimeDifference();

  const currentDiff = getTimeDifferenceInMinutes(
    tripInfo.value.tripWaypoints[0].time,
    tripInfo.value.tripWaypoints[1].time,
  );

  if (
    currentDiff < timeBetween ||
    !tripInfo.value.tripWaypoints[0].time ||
    !tripInfo.value.tripWaypoints[1].time
  ) {
    if (index === 0) {
      tripInfo.value.tripWaypoints[1].time = addMinutesToTime(
        tripInfo.value.tripWaypoints[0].time,
        timeBetween,
      );
    }
    if (index === 1) {
      tripInfo.value.tripWaypoints[0].time = subtractMinutesFromTime(
        tripInfo.value.tripWaypoints[1].time,
        timeBetween,
      );
    }
  }

  if (travelRouteLegsCount > 1) {
    let timeFromStart = Math.ceil(
      travelRouteInfo.value.legs[0].summary.time / 60,
    );
    let timeToEnd = Math.ceil(travelRouteInfo.value.legs[2].summary.time / 60);

    // Calculate time from start place
    tripInfo.value.travelTimeStart = subtractMinutesFromTime(
      tripInfo.value.tripWaypoints[0].time,
      timeFromStart,
    );

    // Calculate time to finish place
    tripInfo.value.travelTimeFinish = addMinutesToTime(
      tripInfo.value.tripWaypoints[1].time,
      timeToEnd,
    );
  }
};

// Function to calculate the buffer time
const calculateBufferTime = () => {
  // Check if at least one passenger needs assistance or is disabled, using optional chaining
  const hasPassengerSpecialNeeds = tripInfo.value.passengers?.some(
    (passenger) =>
      passenger.assistantNeeded === true || passenger.disabled === true,
  );

  if (hasPassengerSpecialNeeds) {
    return 10;
  }

  return 6;
};

/*
  Handle route mode
*/

const routeMode = ref();
const availableVehicles = ref([]);

// Function to handle route mode selection
const selectRouteMode = async (mode) => {
  routeMode.value = mode;

  // Reset values when routMode changes
  selectedTravel.value = null;
  travelUpdateInfo.value = null;
  availableTravels.value = [];
  overlapingTravels.value = [];

  // Create new Travel
  if (mode === "create") {
    try {
      // Count regular and disabled passengers separately
      const passengersRegularCount = tripInfo.value.passengers.filter(
        (passenger) => !passenger.disabled,
      ).length;
      const passengersDisabledCount = tripInfo.value.passengers.filter(
        (passenger) => passenger.disabled,
      ).length;

      // Check available vehicles
      availableVehicles.value = await vehicleStore.checkVehicleAvailability({
        date: tripInfo.value.tripDate,
        passengersRegularCount: passengersRegularCount,
        passengersDisabledCount: passengersDisabledCount,
      });
      await authStore.fetchUsers();
    } catch (error) {
      console.error("Failed to fetch info");
      // Handle error - maybe show a notification to user
    }
  } else {
    tripInfoReview.value = false;
  }
};

/*
  Handle vehicle select
*/

// Use vehicle store
const vehicleStore = useVehicleStore();
const selectedVehicle = ref({});

const handleVehicleSelection = async (vehicle) => {
  selectedVehicle.value = vehicle;
  tripInfo.value.selectedVehicle = selectedVehicle.value.id;
  tripInfo.value.selectedVehicleInfo =
    selectedVehicle.value.reg_nr + ", " + selectedVehicle.value.model;

  // Remove selected driver
  tripInfo.value.selectedDriver = null;
  tripInfo.value.selectedDriverInfo = null;

  // Reset info
  tripInfoReview.value = false;
  overlapingTravels.value = null;

  // Add vehicle address and coordinates as travel info
  tripInfo.value.travelAddress = selectedVehicle.value.address;
  tripInfo.value.travelLat = selectedVehicle.value.lat;
  tripInfo.value.travelLng = selectedVehicle.value.lng;

  // Recalculate route
  updateTravelRoute();

  // Find available drivers
  availableDrivers.value = await driverStore.checkDriversAvailability({
    date: tripInfo.value.tripDate,
    selectedVehicle: selectedVehicle.value.id,
  });
};

/*
  Handle driver select
*/
const authStore = useAuthStore();
const availableDrivers = ref([]);
const selectedDriver = ref({});

const handleDriverSelection = (driver) => {
  // Reset info
  tripInfoReview.value = false;
  overlapingTravels.value = null;

  selectedDriver.value = driver;
  tripInfo.value.selectedDriver = selectedDriver.value.id;
  tripInfo.value.selectedDriverInfo =
    selectedDriver.value.name +
    " " +
    selectedDriver.value.surname +
    ", " +
    selectedDriver.value.phone;
};

/*
  Handle travel select
*/

const selectedTravel = ref();
const travelTimeLineInfo = ref();

// Select travel and parce travel info by id
const selectTravel = async (travelId) => {
  selectedTravel.value = await travelStore.fetchTravelById(travelId);
  checkTripAddAvailability();
};

/*
  Handel travel save
*/

// Save as a new trip and travel
const saveTrip = async (createNewBased = false, isReturnTrip = false) => {
  if (tripStore.isLoading) return;
  try {
    const result = await tripStore.registerTrip(tripInfo.value);
    // Use the toast methods directly
    toast.value.success(
      "Kelionė, pavėžėjimas ir susijusi informacija sėkmingai išsaugota",
    );
    if (createNewBased) {
      // Route to new trip creation with the current trip's ID as template
      setTimeout(() => {
        // Add the return trip parameter if needed
        const returnParam = isReturnTrip ? "?return=true" : "";
        window.location.href = `/trips/create/${result.trip_id}${returnParam}`;
      }, 3000);
    } else {
      // Redirect to created trip
      setTimeout(() => {
        router.replace(`/trips/${result.trip_id}/view`);
      }, 3000);
    }
  } catch (error) {
    toast.value.error(
      "Įvyko klaida. Pasitikrinkite ar visus duomenis esate įvedę",
    );
    throw error;
  }
};

// Save as a new trip added to travel
const addTripToTravel = async (
  createNewBased = false,
  isReturnTrip = false,
) => {
  tripInfo.value.travelId = selectedTravel.value.id;
  tripInfo.value.travelTimeStart =
    travelUpdateInfo.value.combined_waypoints[0].timePlanning;
  tripInfo.value.travelTimeFinish =
    travelUpdateInfo.value.combined_waypoints[
      travelUpdateInfo.value.combined_waypoints.length - 1
    ].timePlanning;
  tripInfo.value.travelLength = travelUpdateInfo.value.summary.length;

  try {
    const result = await tripStore.registerToTravel(tripInfo.value);
    // Use the toast methods directly
    toast.value.success(
      "Kelionė atnaujinta, pavėžėjimas ir susijusi informacija sėkmingai išsaugota",
    );
    if (createNewBased) {
      // Route to new trip creation with the current trip's ID as template
      setTimeout(() => {
        // Add the return trip parameter if needed
        const returnParam = isReturnTrip ? "?return=true" : "";
        window.location.href = `/trips/create/${result.trip_id}${returnParam}`;
      }, 3000);
    } else {
      // Redirect to created trip
      setTimeout(() => {
        router.replace(`/trips/${result.trip_id}/view`);
      }, 3000);
    }
  } catch (error) {
    toast.value.error(
      "Įvyko klaida. Pasitikrinkite ar visus duomenis esate įvedę",
    );
    throw error;
  }
};

/*
  Handle validation
*/

// Check travel availability by date
const availableTravels = ref([]);
const availableTravelsByDate = ref([]);

const checkAvailableTravels = async () => {
  // Reset prev planning info
  travelTimeLineInfo.value = null;
  travelUpdateInfo.value = null;

  // Only proceed with passenger counts if there are passengers
  const params = {
    date: tripInfo.value.tripDate,
  };

  const paramsDate = {
    date: tripInfo.value.tripDate,
  };

  const waypoints = tripInfo.value.tripWaypoints;

  if (
    tripInfo.value.passengers?.length &&
    waypoints?.length &&
    waypoints[0]?.time &&
    waypoints.at(-1)?.time &&
    routeMode.value
  ) {
    // Add persons count from passengers if they exist
    const passengersRegularCount = tripInfo.value.passengers.filter(
      (passenger) => !passenger.disabled,
    ).length;
    const passengersDisabledCount = tripInfo.value.passengers.filter(
      (passenger) => passenger.disabled,
    ).length;
    const totalAssistantCount = tripInfo.value.passengers.reduce(
      (sum, passenger) => sum + (passenger.assistantPersons || 0),
      0,
    );

    params.passengersRegularCount =
      passengersRegularCount + totalAssistantCount;
    params.passengersDisabledCount = passengersDisabledCount;

    // Add start and finish times from waypoints if they exist
    params.startTime = waypoints[0].time;
    params.finishTime = waypoints.at(-1).time;

    // Check available travels
    availableTravels.value = await travelStore.checkAvailability(params);

    if (availableTravels.value.travels.length < 1) {
      toast.value.error(availableTravels.value.message, 8000);
    }
  }

  availableTravelsByDate.value = await travelStore.checkAvailability(
    paramsDate,
  );
};

watch(
  () => tripInfo.value.tripDate,
  async (newDate) => {
    if (!newDate) return;
    availableTravels.value = [];
    availableTravelsByDate.value = [];
    availableVehicles.value = [];
    availableDrivers.value = [];
    overlapingTravels.value = [];
    routeMode.value = null;
    tripInfo.value.selectedDriver = null;
    tripInfo.value.selectedVehicle = null;
    tripInfoReview.value = false;
    try {
      await checkAvailableTravels();
    } catch (error) {
      // Handle any potential errors here
      console.error("Error checking available travels");
    }
  },
);

// Check new trip add possibility to existing travel
const travelUpdateInfo = ref(null);

const checkTripAddAvailability = async () => {
  try {
    travelUpdateInfo.value = await travelStore.checkTripAddAvailability(
      selectedTravel.value.waypoints,
      tripInfo.value.tripWaypoints.map((waypoint) => ({
        ...waypoint,
        type: waypoint.type || "planning",
      })),
    );

    const params = {
      date: tripInfo.value.tripDate,
      driverId: selectedTravel.value.driver.id,
      vehicleId: selectedTravel.value.vehicle.id,
      startTime: travelUpdateInfo.value.combined_waypoints[0].timePlanning,
      finishTime:
        travelUpdateInfo.value.combined_waypoints[
          travelUpdateInfo.value.combined_waypoints.length - 1
        ].timePlanning,
      excludeTravelId: selectedTravel.value.id,
    };

    // Check travels overlaping
    overlapingTravels.value = await travelStore.checkTravelsOverlap(params);

    if (!overlapingTravels.value.hasConflict) {
      travelTimeLineInfo.value = travelUpdateInfo.value.combined_waypoints;
      if (travelUpdateInfo.value.is_valid) {
        if (travelUpdateInfo.value.summary.length > 400) {
          toast.value.warning(
            "Įtraukti pavėžėjimą į kelionę pavyksta, tačiau kelionės atstumas didesnis nei 400 km",
            5000,
          );
        } else {
          toast.value.success("Įtraukti pavėžėjimą į kelionę pavyksta", 5000);
        }
      } else {
        toast.value.error(
          "Suplanuoti pavėžėjimo nepavyksta pasirinktoje kelionėje. Bandykite pakoreguoti planuojamo pavėžėjimo laikus",
          5000,
        );
      }
    }
  } catch (error) {
    toast.value.error(
      "Įvyko klaida skaičiuojant maršrutą. Bandykite dar kartą.",
    );

    throw error;
  }
};

/*
  Prefill form if creation is based on previous created trip
*/

// Helper function for route calculation
const calculateRouteFromWaypoints = async (waypoints) => {
  if (!waypoints || waypoints.length < 2) return null;

  tripStore.isLoading = true;
  try {
    const result = await calculateRoute({
      waypoints: waypoints,
      travelPoint:
        tripInfo.value.travelLat && tripInfo.value.travelLng
          ? {
              lat: tripInfo.value.travelLat,
              lng: tripInfo.value.travelLng,
            }
          : null,
    });

    return result;
  } catch (error) {
    console.error("Error calculating route");
    return null;
  } finally {
    tripStore.isLoading = false;
  }
};

onMounted(async () => {
  await referenceDataStore.ensureDataLoaded();

  let tripId = null;
  // Check for return trip parameter in the URL
  const isReturnTrip = route.query.return === "true";

  if (props.mode === "edit") {
    tripId = props.tripId;
  } else {
    tripId = route.params.tripId;
  }

  // Check if we have a tripId in the URL
  if (tripId) {
    try {
      // Fetch existing trip data
      const existingTrip = await tripStore.fetchTripById(tripId);

      if (props.mode === "edit") {
        // Do not allow edit trip in travel
        if (existingTrip.travelId) {
          router.push("/trips/create");
        } else {
          tripInfo.value = {
            ...tripInfo.value, // Keep default values for other fields
            tripId: existingTrip.id,
            tripDate: existingTrip.tripDate,
            basisType: existingTrip.basisType,
            tripWaypoints: existingTrip.tripWaypoints,
            passengers: existingTrip.clients.map(transformPassenger),
            organization: existingTrip.organization,
          };

          // Calculate route if waypoints exist
          if (tripInfo.value.tripWaypoints?.length > 1) {
            const result = await calculateRouteFromWaypoints(
              tripInfo.value.tripWaypoints,
            );
            if (result) {
              tripInfo.value.tripRoute = result.summary;
              routeGeometry.value = result.geometry;
            }
          }
        }
      } else {
        // Initialize fields to copy based on whether this is a return trip
        const fieldsToUpdate = {
          ...tripInfo.value, // Keep default values for other fields
          tripDate: existingTrip.date,
          basisType: existingTrip.basis_type,
          passengers: existingTrip.clients.map(transformPassenger),
          send_sms_registration: true,
          send_sms_reminder: true,
        };

        // If it's a return trip, also include the waypoints but reverse them
        if (isReturnTrip && existingTrip.tripWaypoints) {
          fieldsToUpdate.tripWaypoints = existingTrip.tripWaypoints
            .map((waypoint) => ({
              ...waypoint,
              time: null, // Remove the time attribute
            }))
            .reverse();
        }

        // Update trip info
        tripInfo.value = fieldsToUpdate;

        // Calculate route if it's a return trip or any other scenario where we have waypoints
        if (tripInfo.value.tripWaypoints?.length > 1) {
          const result = await calculateRouteFromWaypoints(
            tripInfo.value.tripWaypoints,
          );
          if (result) {
            tripInfo.value.tripRoute = result.summary;
            routeGeometry.value = result.geometry;
          }
        }
      }
    } catch (error) {
      console.error("Error fetching trip data");
      // Handle error appropriately
    }
  }
});

const transformPassenger = (apiPassenger) => {
  return {
    id: apiPassenger.id,
    firstName: apiPassenger.name,
    lastName: apiPassenger.surname,
    phone: apiPassenger.phone,
    email: apiPassenger.email || "",
    address: apiPassenger.address || "",
    valid_until: apiPassenger.valid_until || "",
    service_restricted: apiPassenger.service_restricted,
    service_restriction_until: apiPassenger.service_restriction_until,
    disabled: apiPassenger.disabled,
    child_seat_needed: apiPassenger.child_seat_needed,
    wheelchair_needed: apiPassenger.wheelchair_needed,
    description: apiPassenger.description || "",
  };
};

/*
  Form setps validation
*/

function validateStep(step) {
  switch (step) {
    // Form wizard steps validation
    case 1:
      if (tripInfo.value.tripDate && tripInfo.value.basisType) return true;
      return false;
    case 2:
      if (tripInfo.value.passengers.length > 0) return true;
      return false;
    case 3:
      if (tripInfo.value.tripWaypoints.length > 1) return true;
      return false;
    case 4:
      if (
        tripInfoReview.value &&
        tripInfo.value.selectedVehicle &&
        tripInfo.value.selectedDriver &&
        overlapingTravels.value &&
        !overlapingTravels.value.hasConflict
      )
        return true;
      return false;
    case 5:
      if (travelUpdateInfo.value) return true;
      return false;

    // Other validation use case
    case "checkWaypointTime":
      if (tripInfo.value.tripWaypoints.length === 0) {
        return false;
      }
      return tripInfo.value.tripWaypoints.every(
        (waypoint) => waypoint.time && waypoint.time.trim() !== "",
      );
    default:
      return false;
  }
}
</script>

<style scoped></style>
